import { Box, Button, Stack } from '@mui/material';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';

import { useCreateTransactionMutation, useGetDashboardOperationsQuery } from '@/api/v1';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import { AmountAndCurrencyWidget } from '@/components/organisms/AmountAndCurrencyWidget';
import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import { TransactionDetailsAccordion } from '@/components/organisms/TransactionDetailsAccordion';
import { useBalances } from '@/hooks/useBalances';
import { useDisplayUsdcAsUsd } from '@/hooks/useDisplayUsdcAsUsd';
import { useLocalizedFormatter } from '@/hooks/useLocalizedFormatter';
import { useSimulation } from '@/hooks/useSimulation';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';
import {
  ConvertModalAction,
  convertModalReducer,
  ConvertModalState,
  fromAmountChanged,
  initialState,
  stateUpdated,
  SubmitError,
  validationErrorChanged,
} from '@/Screens/Dashboard/Flows/ConvertFlow/reducer';
import { DetailedBalance } from '@/types/bankInfo';
import {
  CurrencyCodeType,
  DashboardOperation,
  DashboardOperationDestination,
  PaymentRailType,
} from '@/types/currency';
import { MarketExecutionType, TransactionRequest } from '@/types/transaction';
import { AsAnyActionArg } from '@/utility/reducers';

interface Props {
  onClose(): void;
}

export const ConvertModal: React.FC<Props> = (props: Props): React.ReactElement => {
  const { onClose } = props;

  const toast = useToast();
  const tr = useTranslator();
  const displayUsdcAsUsd = useDisplayUsdcAsUsd();

  const [createTransaction, createTransactionResult] = useCreateTransactionMutation();
  const [state, dispatch] = React.useReducer<ConvertModalState, AsAnyActionArg<ConvertModalAction>>(
    convertModalReducer as any,
    initialState,
  );
  const { simulationId, fromAmount, fromCurrency, toCurrency } = state;

  const [simulation, simulating] = useSimulation({
    simulationId: simulationId,
    fromAmount: fromAmount,
    toAmount: null,
    fromCurrency: fromCurrency,
    toCurrency: toCurrency,
    fundingMethod: null,
  });

  const { data: flows = [], status } = useGetDashboardOperationsQuery('EXCHANGE');

  const balances = useBalances({
    currencies: [CurrencyCodeType.USDT, CurrencyCodeType.USDC],
  });

  const formatter = useLocalizedFormatter({
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const currentBalance = React.useMemo(
    (): DetailedBalance => balances[fromCurrency] ?? DetailedBalance.zero(fromCurrency),
    [balances, fromCurrency],
  );

  const formattedBalance = React.useMemo((): string => {
    return formatter.format(currentBalance.available);
  }, [formatter, currentBalance]);

  // This useEffect preloads the first operation's data into the state,
  // based from the backend FoF response.
  // Not pretty, but it works.
  React.useEffect((): void => {
    if (status === QueryStatus.fulfilled) {
      if (flows.length) {
        const operation = flows[0];
        const destinationCurrency = operation.destinations[0].currency;

        dispatch(
          stateUpdated({
            fromCurrency: flows[0].currency,
            toCurrency: destinationCurrency,
            paymentRail: operation.destinations[0].rail,
            fundingMethods: operation.destinations
              .filter(
                (destination: DashboardOperationDestination): boolean =>
                  destination.currency === destinationCurrency,
              )
              .map(
                (destination: DashboardOperationDestination): readonly PaymentRailType[] =>
                  destination.fundingMethods ?? [],
              )
              .flat(),
            selectedFundingMethod:
              operation.destinations.length === 1
                ? operation.destinations[0].fundingMethods[0]
                : PaymentRailType.unset,
          }),
        );
      }
    }
  }, [flows, dispatch, status]);

  React.useEffect((): void => {
    switch (createTransactionResult.status) {
      case QueryStatus.fulfilled:
        onClose();
        toast.show(tr('CONVERT_SUCCESS'), 'success');
        break;
      case QueryStatus.rejected:
        toast.show(tr('AN_ERROR_OCCURRED'), 'error');
        break;
      default:
        break;
    }
  }, [createTransactionResult.status, toast, onClose, tr]);

  const fromCurrencies = React.useMemo(
    (): readonly CurrencyCodeType[] =>
      flows.map((operation: DashboardOperation): CurrencyCodeType => operation.currency),
    [flows],
  );

  const toCurrencies = React.useMemo(
    (): readonly CurrencyCodeType[] =>
      flows
        .find((operation: DashboardOperation): boolean => fromCurrency === operation.currency)
        ?.destinations.map(
          (destination: DashboardOperationDestination): CurrencyCodeType => destination.currency,
        ) ?? [],
    [flows, fromCurrency],
  );

  const onFromAmountChange = React.useCallback(
    (value: number | null): void => {
      if (value === state.fromAmount) {
        return;
      }

      dispatch(fromAmountChanged(value));
    },
    [state.fromAmount, dispatch],
  );

  const onFromCurrencyChange = React.useCallback(
    (value: CurrencyCodeType): void => {
      const operation = flows.find(
        (operation: DashboardOperation): boolean => operation.currency === value,
      );

      if (!operation || !operation.destinations?.[0]?.currency) {
        throw new Error(`no flow found for currency: ${value}`);
      }

      const destinationCurrency = operation.destinations[0].currency;

      dispatch(
        stateUpdated({
          fromCurrency: value,
          toCurrency: destinationCurrency,
          paymentRail: operation.destinations[0].rail,
        }),
      );
    },
    [flows, dispatch],
  );

  const onMaxClick = React.useCallback((): void => {
    dispatch(fromAmountChanged(currentBalance.available));
  }, [currentBalance.available]);

  const onSubmit = React.useCallback((): void => {
    if (!state.fromAmount) {
      dispatch(validationErrorChanged(SubmitError.REQUIRED_AMOUNT));
      return;
    }

    if (state.fromAmount > currentBalance.available) {
      dispatch(validationErrorChanged(SubmitError.AMOUNT_BIGGER_THAN_BALANCE));
      return;
    }

    if (!simulation.id || simulation.id === '') {
      console.debug('no simulation id');
      return;
    }

    const transactionRequest: TransactionRequest = {
      simulationId: simulation.id,
      beneficiaryIp: '0.0.0.0',
      marketExecutionType: MarketExecutionType.executeImmediately,
    };
    createTransaction(transactionRequest);
  }, [simulation, state, dispatch, createTransaction, currentBalance.available]);

  const error = React.useMemo(
    (): boolean =>
      state.validationError === SubmitError.REQUIRED_AMOUNT ||
      state.validationError === SubmitError.AMOUNT_BIGGER_THAN_BALANCE,
    [state.validationError],
  );

  const helperText = React.useMemo(
    (): string | undefined =>
      state.validationError === SubmitError.REQUIRED_AMOUNT
        ? tr('REQUIRED_AMOUNT')
        : state.validationError === SubmitError.AMOUNT_BIGGER_THAN_BALANCE
          ? tr('AMOUNT_BIGGER_THAN_BALANCE', formattedBalance)
          : undefined,
    [formattedBalance, state.validationError, tr],
  );

  React.useEffect((): VoidFunction => {
    return (): void => {
      toast.remove();
    };
  }, [toast]);

  return (
    <ApplicationModal title={tr('CONVERT_ACCOUNT_BALANCE')} back=".." onClose={onClose}>
      <Box sx={styles.inputsContainer} mb={3}>
        <AmountAndCurrencyWidget
          label={tr('START_CURRENCY')}
          size="normal"
          error={error}
          helperText={helperText}
          amount={fromAmount}
          currency={fromCurrency}
          currencies={fromCurrencies}
          onCurrencyChange={onFromCurrencyChange}
          onAmountChange={onFromAmountChange}
        />
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body1" color="custom.LightGray">
            {tr(displayUsdcAsUsd ? 'WALLET_BALANCE_LEGACY' : 'WALLET_BALANCE', formattedBalance)}
          </Typography>
          <Button
            variant="dark"
            size="small"
            sx={styles.maxButton}
            disabled={false}
            onClick={onMaxClick}>
            {tr('MAX')}
          </Button>
        </Box>
        <AmountAndCurrencyWidget
          label={tr('CONVERTED_CURRENCY')}
          size="normal"
          amount={simulation.to.value}
          currency={toCurrency}
          currencies={toCurrencies}
          disabled={simulating}
          placeholder={simulating}
          readOnly={true}
        />
      </Box>

      <TransactionDetailsAccordion
        simulating={simulating}
        fromAmount={fromAmount}
        fromCurrency={fromCurrency}
        simulation={simulation}
      />

      <Stack alignItems="center" mt={5} gap={1}>
        <LoadingButton
          variant="contained"
          loading={simulating || createTransactionResult.isLoading}
          fullWidth={true}
          onClick={onSubmit}>
          {tr('CONVERT')}
        </LoadingButton>
        <Button variant="text" onClick={onClose} disableRipple>
          {tr('CANCEL')}
        </Button>
      </Stack>
    </ApplicationModal>
  );
};

const styles: Record<string, SystemStyleObject> = {
  inputsContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    mt: 3,
  },
  swapButton: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    padding: 0,
    width: 40,
    height: 40,
    mt: '-20px',
    ml: '-20px',
  },
};
